import { DocumentCollection, DocumentResource, Resource } from 'ngx-jsonapi';
import { BankStrategy } from 'common/bank-selection/domain/bank-strategy.enum';
import { CountryEnum } from 'common/domain/country/entities/country.enum';
import { Currency } from 'common/systems/domain/resources/currency.resource';
import { Network } from 'common/systems/domain/resources/network.resource';
import { Rate } from 'common/systems/domain/resources/rate.resource';

/**
 * Framework-agnostic `System` resource for the `systems` capability
 * (single source of truth for `System` state, see proposal.md of
 * `systems-capability`). Extends ngx-jsonapi's `Resource` with no Angular
 * dependency, and preserves `type`/`attributes`/the `rates` and `currency`
 * relationships of the legacy `@app/core/resources/system.ts`, using
 * `Rate`/`Currency` types owned by `common/systems` (see design.md
 * Decision 1: attributes kept 1:1 for the future `deals-bag` consumer).
 *
 * This resource does not import anything from `@app/core/resources/*`; the
 * legacy tree stays untouched (Non-Goal of `systems-capability`).
 */
export class System extends Resource {
    public type: string = 'systems';
    public attributes: {
        minimum_amount_send: number | null;
        minimum_amount_receive: number | null;
        group_id: string;
        name: string;
        can_send: boolean;
        can_receive: boolean;
        fee_send: number;
        fee_receive: number;
        account_required_send: boolean;
        account_required_receive: boolean;
        alternative_name: string;
        account_type: string;
        market: string;
        domestic_country: CountryEnum;
        replacement_system_id: string;
        decimal_places: number;
        trend: number;
        fixed_fee_send: number;
        fixed_fee_receive: number;
        default_network_id: string | null;
        percent_fee_send: number;
        percent_fee_receive: number;
        currency_id: string;
        bank_strategy: BankStrategy;
        bank_required: boolean;
    } = {
        minimum_amount_send: null,
        minimum_amount_receive: null,
        group_id: '',
        name: '',
        can_send: false,
        can_receive: false,
        fee_send: 0,
        fee_receive: 0,
        account_required_send: false,
        account_required_receive: false,
        alternative_name: '',
        account_type: '',
        market: '',
        domestic_country: CountryEnum.INTERNATIONAL,
        replacement_system_id: '',
        decimal_places: 2,
        trend: 0,
        fixed_fee_send: 0,
        fixed_fee_receive: 0,
        default_network_id: null,
        percent_fee_send: 0,
        percent_fee_receive: 0,
        currency_id: '',
        bank_strategy: BankStrategy.NONE,
        bank_required: false,
    };

    public relationships: {
        rates: DocumentCollection<Rate>;
        currency: DocumentResource<Currency>;
        networks: DocumentCollection<Network>;
    } = {
        rates: new DocumentCollection<Rate>(),
        currency: new DocumentResource<Currency>(),
        networks: new DocumentCollection<Network>(),
    };
}
